/**
 * useUniversalExport Hook
 *
 * Core business logic hook for universal export system.
 * Manages state, localStorage persistence, and export execution.
 *
 * FEATURES:
 * - Debounced localStorage writes (300ms)
 * - Memoized callbacks
 * - Validation before export
 * - Serialization-safe storage (strips React elements before persist, rehydrates on read)
 */

import { useCallback, useMemo } from 'react';

import type {
  ExportableEntity,
  ExportFormat,
  ExportModalState,
  ExportOption,
  UniversalExportConfig,
  UseUniversalExportReturn,
} from '@/components/export/universal/types';
import {
  hasHardFailures,
  rehydrateState,
  stripNonSerializableProps,
  validateExport,
} from '@/components/export/universal/utils';
import { useAsyncExports } from '@/hooks/useAsyncExports';
import type { GraphQuery } from '@/types';
import { useStorage } from '@/utils/storage/useStorage.util';

/**
 * Universal Export Hook
 *
 * @param config - Entity-specific export configuration
 * @param activeQuery - Optional pre-built graph query from table filters
 * @returns Hook interface with state and actions
 *
 * USAGE:
 * ```typescript
 * const exportHook = useUniversalExport(assetExportConfig, activeQuery);
 *
 * // Update selections
 * exportHook.updateFilter('status', statusOptions);
 * exportHook.updateColumns(selectedColumns);
 *
 * // Execute export
 * await exportHook.executeExport(selectedRowKeys);
 * ```
 */
export function useUniversalExport<TEntity extends ExportableEntity>(
  config: UniversalExportConfig<TEntity>,
  activeQuery?: GraphQuery
): UseUniversalExportReturn<TEntity> {
  // Freeze storageKey on first render to prevent useStorage key changing error.
  // config object reference may change even though storageKey string remains constant.
  const storageKey = useMemo(() => config.storageKey, []);

  // Build default state from config (includes icons for rendering)
  const defaultStateWithIcons: ExportModalState = useMemo(() => {
    const filters: Record<string, readonly ExportOption[]> = {};

    // Initialize filters with defaults from config
    config.filterSections.forEach(section => {
      filters[section.id] = section.defaultSelected || section.options;
    });

    // Initialize columns with defaults from config
    const columns = config.columns
      .filter(col => col.defaultSelected !== false)
      .map(col => ({ label: col.label, value: col.id }));

    return {
      filters,
      columns,
      format: config.defaultFormat,
    };
  }, [config]);

  // Strip non-serializable props (icons) for storage default
  const defaultState = useMemo(
    () => stripNonSerializableProps(defaultStateWithIcons),
    [defaultStateWithIcons]
  );

  // Persist state to localStorage with debouncing (stores stripped state)
  const [rawState, setRawState] = useStorage<ExportModalState>(
    { key: storageKey },
    defaultState
  );

  // Rehydrate icons from config for rendering
  const state: ExportModalState = useMemo(
    () => rehydrateState(rawState, config.filterSections),
    [rawState, config.filterSections]
  );

  // Wrapper that strips non-serializable props before writing to storage
  const setState = useCallback(
    (
      updater: ExportModalState | ((prev: ExportModalState) => ExportModalState)
    ) => {
      if (typeof updater === 'function') {
        setRawState(prev => {
          // Rehydrate prev so the updater callback sees icons
          const rehydratedPrev = rehydrateState(prev, config.filterSections);
          const nextState = updater(rehydratedPrev);
          return stripNonSerializableProps(nextState);
        });
      } else {
        setRawState(stripNonSerializableProps(updater));
      }
    },
    [setRawState, config.filterSections]
  );

  // Export mutation (with toast)
  const { mutateAsync: exportData, isPending: isExporting } = useAsyncExports(
    config.apiEntityType
  );

  // Update filter selection
  const updateFilter = useCallback(
    (sectionId: string, options: readonly ExportOption[]) => {
      setState(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          [sectionId]: options,
        },
      }));
    },
    [setState]
  );

  // Update column selection
  const updateColumns = useCallback(
    (options: readonly ExportOption[]) => {
      setState(prev => ({
        ...prev,
        columns: options,
      }));
    },
    [setState]
  );

  // Update export format
  const updateFormat = useCallback(
    (format: ExportFormat) => {
      setState(prev => ({
        ...prev,
        format,
      }));
    },
    [setState]
  );

  // Check if export should be disabled
  const isExportDisabled = useCallback(
    (selectedRows: readonly string[]) => {
      // Use custom validation if provided
      if (config.validateExport) {
        const errors = config.validateExport(
          state.filters,
          state.columns,
          selectedRows
        );
        return hasHardFailures(errors);
      }

      // Default validation
      const errors = validateExport(
        state.filters,
        state.columns,
        selectedRows,
        {
          requireFiltersWhenNoSelection: !activeQuery,
        }
      );

      return hasHardFailures(errors);
    },
    [config, state, activeQuery]
  );

  // Execute export
  const executeExport = useCallback(
    async (selectedRows: readonly string[]) => {
      // Validate before export
      if (config.validateExport) {
        const errors = config.validateExport(
          state.filters,
          state.columns,
          selectedRows
        );
        if (hasHardFailures(errors)) {
          const firstError = errors?.[0];
          throw new Error(firstError?.message || 'Validation failed');
        }
      } else {
        // Use default validation
        const errors = validateExport(
          state.filters,
          state.columns,
          selectedRows,
          {
            requireFiltersWhenNoSelection: !activeQuery,
          }
        );
        if (hasHardFailures(errors)) {
          const firstError = errors?.[0];
          throw new Error(firstError?.message || 'Validation failed');
        }
      }

      // Build query: use activeQuery if provided and no selectedRows, otherwise build from filters
      const query =
        activeQuery && selectedRows.length === 0
          ? {
              node: activeQuery.node,
              ...(activeQuery.filters ? { filters: activeQuery.filters } : {}),
            }
          : config.buildQuery(state.filters, selectedRows);

      // Build column list
      const columns =
        state.columns.length > 0
          ? state.columns.map(col => col.value as string)
          : undefined;

      const exportParams = {
        query,
        columns,
        items: selectedRows.length > 0 ? (selectedRows as string[]) : undefined,
        format: state.format.value,
      };

      // Use custom export handler if provided
      if (config.customExportHandler) {
        await config.customExportHandler(exportParams);
        return;
      }

      await exportData({
        query,
        columns,
        items: exportParams.items,
        format: state.format.value,
      });
    },
    [config, state, exportData, activeQuery]
  );

  return {
    state,
    updateFilter,
    updateColumns,
    updateFormat,
    executeExport,
    isExportDisabled,
    isLoading: isExporting,
  };
}
